import { clip } from '../../../../common/src/ts/utils';

export interface StringView {
  readonly length: number;
  read(length?: number): string;
  split(index: number): [StringView, StringView];
  slice(start?: number, end?: number): StringView;
}

export class SingleStringView implements StringView {
  private readonly start: number;
  private readonly end: number;
  public readonly length: number;

  public constructor(private readonly str: string, start: number = 0, end: number = str.length) {
    const { length } = str;
    this.start = clip(start, 0, length, 0, length);
    this.end = clip(end, 0, length, this.start, length);
    this.length = this.end - this.start;
  }

  public read(length: number = this.length) {
    return this.str.slice(this.start, Math.min(this.start + length, this.end));
  }

  public split(index: number): [StringView, StringView] {
    return [this.slice(0, index), this.slice(index)];
  }

  public slice(start: number = 0, end: number = this.length) {
    const { length } = this;
    const startOffset = clip(start, 0, length, 0, length);
    const endOffset = clip(end, 0, length, startOffset, length);
    return new SingleStringView(this.str, this.start + startOffset, this.start + endOffset);
  }
}

export class MultiStringView implements StringView {
  private len: number = 0;
  private list: [number, StringView][] = [];

  public get length() {
    return this.len;
  }

  public constructor(...args: (string | StringView)[]) {
    this.append(...args);
  }

  public append(...args: (string | StringView)[]) {
    let length = 0;
    for (const item of args) {
      if (item.length === 0) {
        continue;
      }
      if (typeof item === 'string') {
        this.list.push([length, new SingleStringView(item)]);
      } else {
        this.list.push([length, item]);
      }
      length += item.length;
    }
    this.len += length;
  }

  public clear() {
    this.len = 0;
    this.list = [];
  }

  private hit(position: number): [number, number] {
    let index = 0;
    for (const [offset] of this.list) {
      if (position >= offset) {
        return [index, offset];
      }
      index += 1;
    }
    return [index, this.length];
  }

  public read(length: number = this.length): string {
    let rest = length;
    const result: string[] = [];
    for (const [, item] of this.list) {
      if (rest <= 0) {
        break;
      }
      const text = item.read(rest);
      result.push(text);
      rest -= text.length;
    }
    return result.join('');
  }

  public split(index: number): [StringView, StringView] {
    return [this.slice(0, index), this.slice(index)];
  }

  public slice(start: number = 0, end: number = this.length): StringView {
    const { length } = this;
    const startOffset = clip(start, 0, length, 0, length);
    const endOffset = clip(end, 0, length, startOffset, length);
    const [index, offset] = this.hit(startOffset);
    if (index >= this.list.length) {
      return new MultiStringView();
    }
    const [endIndex] = end === undefined ? [this.list.length] : this.hit(endOffset);
    if (index === endIndex) {
      return this.list[index][1].slice(startOffset - offset, endOffset - offset);
    }
    const result = [this.list[index][1].slice(startOffset - offset)];
    for (let i = index + 1; i < endIndex; i += 1) {
      const [offset, view] = this.list[i];
      result.push(view.slice(Math.max(0, startOffset - offset)));
    }
    if (endIndex < this.list.length) {
      const [offset, view] = this.list[endIndex];
      result.push(view.slice(Math.max(0, startOffset - offset), endOffset - offset));
    }
    return new MultiStringView(...result);
  }

  public toString() { return this.read(); }
}
